import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getPurchaseSummary } from "../../services/purchaseService";
import { formatCurrency } from "../../utils/currency";
import type { PurchaseSummary as PurchaseSummaryType } from "../../types/models";
import { getApiErrorMessage } from "../../utils/apiError";

/**
 * Spending Summary dashboard widget (Story 5.6).
 * Total spent, breakdown by category, top 3 suppliers. Click navigates to /purchases.
 */
export function SpendingSummary() {
  const { t } = useTranslation("dashboard");
  const [summary, setSummary] = useState<PurchaseSummaryType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPurchaseSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = getApiErrorMessage(err, t("summary_error_spending"));
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClick = () => navigate("/purchases");

  if (loading) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left flex flex-col items-center justify-center py-8 text-center hover:opacity-90 transition-opacity"
      >
        <div className="w-8 h-8 border-2 border-secondary-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("summary_loading_spending")}</p>
      </button>
    );
  }

  if (error) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left py-4 text-center hover:opacity-90 transition-opacity"
      >
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </button>
    );
  }

  if (!summary) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left py-4 text-center hover:opacity-90 transition-opacity"
      >
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("summary_no_spending_data")}</p>
      </button>
    );
  }

  const byCategory = summary.by_category ?? {
    materials: 0,
    equipment: 0,
    operational: 0,
  };
  const totalSpent = Number(summary.total_spent ?? 0);
  const topSuppliers = (summary.top_suppliers ?? []).slice(0, 3);
  const categoryLabels: Record<string, string> = {
    materials: t("summary_category_materials"),
    equipment: t("summary_category_equipment"),
    operational: t("summary_category_operational"),
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full text-left hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 focus-visible:ring-offset-2 rounded-lg"
    >
      <div className="space-y-3">
        <div className="flex justify-between items-baseline gap-2">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {t("summary_total_spent_all_time")}
          </span>
          <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
            {formatCurrency(totalSpent)}
          </span>
        </div>
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-2">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1.5">{t("summary_by_category")}</p>
          <ul className="space-y-1.5 text-sm">
            {(["materials", "equipment", "operational"] as const).map((key) => (
              <li key={key} className="flex justify-between text-neutral-700 dark:text-neutral-300">
                <span>{categoryLabels[key]}</span>
                <span className="tabular-nums font-medium">
                  {formatCurrency(Number(byCategory[key] ?? 0))}
                </span>
              </li>
            ))}
          </ul>
        </div>
        {topSuppliers.length > 0 && (
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-2">
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1.5">{t("summary_top_suppliers")}</p>
            <ul className="space-y-1.5 text-sm">
              {topSuppliers.map((s, i) => (
                <li
                  key={`${i}-${s.name}`}
                  className="flex justify-between gap-2 text-neutral-700 dark:text-neutral-300"
                >
                  <span className="truncate">{s.name}</span>
                  <span className="tabular-nums font-medium shrink-0">
                    {formatCurrency(Number(s.total))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </button>
  );
}
